/**
 * components/profile/PrivacyAiTab.jsx — v3.7.7 Phase 84.31
 *
 * Personalization + data controls. The old ProfileSection stashed the
 * DangerZone at the bottom of the Account tab, next to name/DOB — that
 * mixed "who I am" with "delete my account" in a way that ran counter
 * to the redesign spec. Phase 84.31 relocates all destructive and
 * privacy actions here.
 *
 * Sections:
 *   • Personalization & AI — a small set of on/off preferences that
 *     control how 2120 uses the user's profile data. Persisted to
 *     Supabase `profiles` when the columns exist; falls back to
 *     localStorage otherwise so the UI still respects the choice.
 *   • Recommendation transparency — a short plain-language paragraph
 *     explaining that 2120 may use profile facts and financial activity
 *     to prioritize insights, plus a link into the "My life & priorities"
 *     tab where the underlying facts live.
 *   • Data controls — export, clear financial data, delete account.
 *     Uses the existing DangerZone component which already implements
 *     the destructive-confirm flow and churn-reason capture.
 *
 * Phase 2 will wire up the AI usage and inferred-fact review lists.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Sparkles, Shield, FileText, Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import {
  SectionHeader, SettingsCard, PrimaryButton, FieldLabel, SavedFlash,
} from '@/components/settings/primitives';
import DangerZone from '@/components/settings/DangerZone';
import AIUsageSection from '@/components/settings/AIUsageSection';

// Persistence key for the AI/personalization preferences fallback.
const LS_KEY = 'twoone20:aiPrefs';
function loadLocal() {
  try { return JSON.parse(window.localStorage.getItem(LS_KEY) || '{}') || {}; }
  catch { return {}; }
}
function saveLocal(next) {
  try { window.localStorage.setItem(LS_KEY, JSON.stringify(next)); } catch { /* private-mode */ }
}

const PREFS = [
  {
    key: 'personalize_with_life_profile',
    label: 'Personalize insights with my Life Profile',
    hint:  'Use household, priorities and preferences to prioritize the insights you see.',
    def:   true,
  },
  {
    key: 'infer_from_transactions',
    label: 'Look for profile hints in my transactions',
    hint:  'Detect signals like variable income or a new dependent from statement activity. 2120 will ask before saving any inferred fact — nothing is confirmed without you.',
    def:   true,
  },
  {
    key: 'ai_influence_tone',
    label: 'Let confirmed profile details influence tone',
    hint:  'Warmer wording when you say you feel stressed; more matter-of-fact wording when you say you feel in control. Never changes any number.',
    def:   true,
  },
];

function Toggle({ checked, onChange, testId, id }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={
        'inline-flex h-6 w-11 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 shrink-0 ' +
        (checked ? 'bg-evergreen' : 'bg-muted')
      }
      data-testid={testId}
      id={id}
    >
      <span
        className={
          'inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ' +
          (checked ? 'translate-x-5' : 'translate-x-0.5')
        }
      />
    </button>
  );
}

export default function PrivacyAiTab() {
  const { user, profile } = useAuth();
  const [prefs, setPrefs] = useState({});
  const [saved, setSaved] = useState(false);
  // v3.7.13 Phase 84.31 Part 7 — Export button loading state so users
  // know the download is being prepared during the 1-3s round trip.
  const [exporting, setExporting] = useState(false);

  // Hydrate from the profile row if the column exists, else localStorage.
  useEffect(() => {
    const local = loadLocal();
    const server = (profile && typeof profile.ai_prefs === 'object') ? profile.ai_prefs : {};
    const next = {};
    PREFS.forEach((p) => {
      next[p.key] = (server[p.key] !== undefined) ? !!server[p.key]
                  : (local[p.key]  !== undefined) ? !!local[p.key]
                  : p.def;
    });
    setPrefs(next);
  }, [profile]);

  const setPref = async (key, value) => {
    const next = { ...prefs, [key]: value };
    setPrefs(next);
    saveLocal(next); // always mirror locally first
    setSaved(true);
    setTimeout(() => setSaved(false), 1800);
    if (user?.id) {
      try {
        const { error } = await supabase.from('profiles').update({ ai_prefs: next }).eq('id', user.id);
        if (error && !/ai_prefs/i.test(error.message || '')) throw error;
      } catch (e) {
        // Column may not exist yet on the preview DB — the mirror
        // above keeps the choice live for this device in the meantime.
        console.debug('ai_prefs persistence deferred', e?.message);
      }
    }
  };

  const doExport = async () => {
    if (!user || exporting) return;
    setExporting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/data/export`, {
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `2120-data-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Export downloaded.');
    } catch (e) {
      toast.error(e?.message || 'Export is not yet available on this environment.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="profile-privacy-tab">
      <SectionHeader
        title="Privacy & AI"
        subtitle="Control how 2120 uses your profile, and what happens to your data."
      />

      {/* ── Personalization & AI ──────────────────────────────────────── */}
      <SettingsCard className="px-6 py-6">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70 mb-5 inline-flex items-center gap-2">
          <Sparkles className="h-3 w-3" /> Personalization &amp; AI
        </p>
        <div className="space-y-5">
          {PREFS.map((p) => (
            <label
              key={p.key}
              htmlFor={`profile-ai-pref-${p.key}`}
              className="flex items-start justify-between gap-4 cursor-pointer"
            >
              <div className="min-w-0">
                <div className="text-sm font-medium text-charcoal">{p.label}</div>
                <div className="text-[12px] text-muted-foreground mt-0.5 leading-relaxed">{p.hint}</div>
              </div>
              <Toggle
                checked={!!prefs[p.key]}
                onChange={(v) => setPref(p.key, v)}
                testId={`profile-ai-pref-${p.key}`}
                id={`profile-ai-pref-${p.key}`}
              />
            </label>
          ))}
        </div>
        <div className="mt-4">
          <SavedFlash visible={saved} />
        </div>
      </SettingsCard>

      {/* ── Recommendation transparency ───────────────────────────────── */}
      <SettingsCard className="px-6 py-6">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70 mb-5 inline-flex items-center gap-2">
          <Shield className="h-3 w-3" /> Recommendation transparency
        </p>
        <p className="text-sm text-charcoal leading-relaxed">
          2120 may use your financial activity, confirmed profile details and planning
          preferences to prioritize the insights you see. You can review, remove or
          decline any profile detail at any time on the{' '}
          <a href="/profile?tab=life" className="text-primary hover:underline" data-testid="profile-privacy-life-link">
            My life &amp; priorities
          </a>{' '}
          tab.
        </p>
        <p className="text-[12px] text-muted-foreground/80 mt-2 leading-relaxed">
          Inferred hints (things 2120 notices from your statements) never become
          confirmed facts until you explicitly agree.
        </p>
      </SettingsCard>

      {/* ── AI usage (kept from the existing Settings hub) ────────────── */}
      <AIUsageSection />

      {/* ── Data controls ─────────────────────────────────────────────── */}
      <SettingsCard className="px-6 py-6" data-testid="profile-data-controls">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70 mb-5 inline-flex items-center gap-2">
          <FileText className="h-3 w-3" /> Your data
        </p>
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <FieldLabel label="Export my data" />
              <p className="text-[12px] text-muted-foreground leading-relaxed">
                Download a JSON copy of your accounts, transactions, goals and profile.
              </p>
            </div>
            <PrimaryButton
              onClick={doExport}
              disabled={exporting}
              testId="profile-data-export"
            >
              {exporting ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                  <span>Preparing…</span>
                  <span className="sr-only" aria-live="polite">Preparing your data export</span>
                </span>
              ) : (
                'Download export'
              )}
            </PrimaryButton>
          </div>
        </div>
      </SettingsCard>

      {/* ── Danger zone (moved from Account & identity) ───────────────── */}
      <DangerZone />
    </div>
  );
}
